import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLines, Reveal, SectionHeading, LeafSprig } from "@/components/Motion";
import { IMAGES } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  { title: "Low-Carbon Catering", body: "Our whole catering model is built to emit less: food prepared once in our permanent kitchen, boxed individually and delivered hot — no kitchen truck parked on your location all day." },
  { title: "No Generator", body: "Because we bring the food, not the kitchen, our service never needs a diesel generator humming away on your set. Nothing to power, nothing to burn." },
  { title: "Electric, Hybrid & Biofuel Deliveries", body: "Deliveries are made by electric or hybrid vehicles wherever the route allows, with biofuel options for the longer runs — and routes planned to keep miles to a minimum." },
  { title: "Compostable Packaging", body: "Every box, cup, lid and fork we send out is compostable — serviceware that breaks down instead of hanging around for centuries." },
  { title: "Food Waste Reduction", body: "Menus are planned to your headcount and dietary list, prep waste is composted, and we keep a close eye on what little reaches landfill so we can keep pushing it down." },
  { title: "Reusing Food at Base", body: "Safe surplus comes back to base rather than to landfill — it becomes staff meals and café specials at Studio House. Good food feeds people, not bins." },
  { title: "Sustainable Sourcing", body: "Local, seasonal and plant-forward, from producers we can name — including the 'too ugly to sell' crops other buyers reject, and ingredients we grow and forage ourselves." },
];

const SustainHero = () => (
  <section className="relative overflow-hidden bg-noir pb-24 pt-36 sm:pb-32" data-testid="sustainability-hero">
    <LeafSprig className="pointer-events-none absolute -left-8 bottom-6 h-72 -rotate-12 text-leaf-light/12" />
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <motion.img
          src="/images/logo-duo.png"
          alt="Root2Shoot — Sustainable Café and Film & TV Catering"
          className="mb-9 h-14 w-auto sm:h-16"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.8 }}
          data-testid="sustainability-logo"
        />
        <motion.p
          className="label-mono flex items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
        >
          Sustainability
          <span className="h-px w-20 bg-gold/50" aria-hidden="true" />
        </motion.p>
        <MaskedLines
          lines={["Not a claim."]}
          className="mt-6 font-editorial text-5xl leading-[1.0] tracking-tight text-cream sm:text-6xl lg:text-7xl"
        />
        <MaskedLines
          as="p"
          lines={["How we operate."]}
          className="font-editorial text-4xl leading-[1.02] tracking-tight text-gold sm:text-5xl lg:text-6xl"
          delay={0.4}
        />
        <motion.p
          className="mt-8 max-w-2xl text-base leading-relaxed text-sage sm:text-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
        >
          Sustainability isn't our marketing department — it's our operating manual. These
          are the seven things we actually do, on every café day and every production job.
        </motion.p>
      </div>
      <motion.div
        className="relative lg:col-span-4 lg:col-start-9"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.45, duration: 1.1, ease: EASE }}
      >
        <div className="absolute -inset-0 translate-x-3 translate-y-3 border border-gold/40" aria-hidden="true" />
        <img
          src={IMAGES.citrusClapperboard}
          alt="A film production clapperboard surrounded by fresh lemons, limes and red chillies — Root2Shoot sustainable film and TV catering"
          className="relative aspect-[4/5] w-full object-cover"
          data-testid="sustainability-hero-image"
        />
      </motion.div>
    </div>
  </section>
);

const Chapters = () => (
  <section className="bg-paper py-24 text-ink sm:py-32" data-testid="sustainability-chapters">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div>
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.title} delay={0.04}>
            <div
              className="group grid gap-3 border-t border-ink/15 py-10 transition-colors duration-500 hover:border-leaf/60 sm:grid-cols-[90px_1fr_1.3fr] sm:items-baseline sm:gap-10"
              data-testid={`sustainability-chapter-${i + 1}`}
            >
              <span className="font-editorial text-5xl leading-none text-leaf/60 transition-colors duration-500 group-hover:text-leaf sm:text-6xl">
                0{i + 1}
              </span>
              <h3 className="font-serif text-2xl tracking-tight text-ink sm:text-3xl">{c.title}</h3>
              <p className="max-w-xl text-sm leading-relaxed text-ink/65 sm:text-base">{c.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-16">
        <div className="grid items-center gap-6 border-y border-gold-deep/40 py-9 sm:grid-cols-[auto_1fr]" data-testid="bafta-note">
          <p className="label-mono-ink">BAFTA ALBERT Principles</p>
          <p className="text-base leading-relaxed text-ink/75">
            Our approach follows the principles of BAFTA ALBERT and the wider sustainability
            direction of the film and television industry.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const Proof = () => (
  <section className="relative overflow-hidden bg-obsidian py-24 sm:py-32" data-testid="sustainability-proof">
    <LeafSprig className="pointer-events-none absolute -right-8 top-10 h-72 rotate-[160deg] text-cream/10" />
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-6">
        <SectionHeading
          kicker="The Proof"
          title={<>Measured, <span className="text-gold">not marketed</span></>}
          className="mb-8"
        />
        <Reveal delay={0.1}>
          <p className="text-base leading-relaxed text-sage sm:text-lg">
            We're building the habit of measuring what we can — waste, packaging, miles —
            and being honest about what the numbers say, good or bad. Still learning, still
            growing, still asking: can we do this better?
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/catering" className="btn-gold" data-testid="sustainability-cta-catering">
              Cater the Sustainable Way <ArrowRight size={16} />
            </Link>
            <Link to="/our-story" className="btn-ghost" data-testid="sustainability-cta-story">
              Meet the Team
            </Link>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.12} className="relative lg:col-span-5 lg:col-start-8">
        <div className="absolute -inset-0 translate-x-3 translate-y-3 border border-gold/40" aria-hidden="true" />
        <div className="relative">
          <img
            src={IMAGES.hotboxRamen}
            alt="Root2Shoot hotbox of crispy pork belly ramen with pak choi, lime and soft egg"
            className="aspect-[4/5] w-full object-cover"
            loading="lazy"
            decoding="async"
            data-testid="sustainability-image"
          />
          <div className="absolute bottom-0 left-0 border-t border-r border-gold/30 bg-noir/90 px-6 py-4">
            <p className="font-display text-lg italic text-gold">Grown close. Cooked slow. Wasted never.</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Sustainability() {
  return (
    <>
      <Seo
        title="Sustainability — Low-Carbon Hotbox Catering, Done Honestly | Root2Shoot"
        siteName="Root2Shoot"
        description="How Root2Shoot actually operates: low-carbon hotbox catering, no generators, electric, hybrid or biofuel deliveries, compostable serviceware, food waste reduction and named, seasonal sourcing."
        image="/images/logo-duo.png"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Sustainability", path: "/sustainability" }]}
        jsonLd={{ "@context": "https://schema.org", "@type": "WebPage", name: "Sustainability — Root2Shoot" }}
      />
      <SustainHero />
      <Chapters />
      <Proof />
    </>
  );
}
